// Ссылка магазина: https://…/s/<код> или ?from=shop:<код> (клиенты продавцов,
// 17.10.2026).
//
// Продавец переводит своих клиентов на OSMIX. Код запоминается на устройстве
// в момент перехода и живёт 30 дней - как партнёрская ссылка ?promo=
// (referral-promo.ts). Дальше:
//   1) переход засчитывается магазину (`/shop-invites/visit`);
//   2) когда покупатель вошёл или зарегистрировался, код уходит в
//      `/buyer/shop-invite` - шлюз привязывает его к магазину, если он новый
//      для площадки и ещё ничей.
// Первое касание: код другого магазина в окне 30 дней прежний не заменяет.
// Партнёрскую ссылку ?promo= это не трогает - хранилища разные.

import type { ReferralVisitMeta } from "@/api/types";
import { referralMetaOf } from "./referral-meta";

const STORAGE_KEY = "osmix-shop-invite";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

// meta - канал (ссылка / QR) и метки первого перехода (27.10.2026).
type Stored = { code: string; at: number; visited?: boolean; attemptedFor?: string; meta?: ReferralVisitMeta };

const read = (): Stored | null => {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const value = JSON.parse(raw) as Stored;
		if (!value?.code || Date.now() - value.at > TTL_MS) {
			window.localStorage.removeItem(STORAGE_KEY);
			return null;
		}
		return value;
	} catch {
		return null;
	}
};

const write = (value: Stored): void => {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
	} catch {
		// Хранилище закрыто - привязки не будет, витрину не роняем.
	}
};

export const normalizeShopInviteCode = (code: string): string =>
	code.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16);

/** Запомнить код магазина. Уже запомненный в окне 30 дней не заменяется (первое касание). */
export const rememberShopInvite = (raw: string, meta?: ReferralVisitMeta): string | null => {
	if (typeof window === "undefined") return null;
	const code = normalizeShopInviteCode(raw);
	if (!code) return null;
	const current = read();
	if (!current) write({ code, at: Date.now(), meta });
	return code;
};

/** ?from=shop:<код> в адресе любой страницы витрины. */
export const captureShopInvite = (search: string): string | null => {
	const from = new URLSearchParams(search).get("from") ?? "";
	if (!from.toLowerCase().startsWith("shop:")) return null;
	return rememberShopInvite(from.slice(5), referralMetaOf(search));
};

export const readShopInvite = (): string | undefined => read()?.code;

/** Канал и метки запомненного перехода - для записи перехода на шлюзе. */
export const readShopInviteMeta = (): ReferralVisitMeta | undefined => read()?.meta;

export const shopInviteVisitPending = (): boolean => {
	const current = read();
	return Boolean(current && !current.visited);
};

export const markShopInviteVisited = (): void => {
	const current = read();
	if (current) write({ ...current, visited: true });
};

/** Привязку для этого аккаунта уже запрашивали - повторять на каждом открытии незачем. */
export const shopInviteBindPendingFor = (userKey: string): string | undefined => {
	const current = read();
	if (!current || current.attemptedFor === userKey) return undefined;
	return current.code;
};

export const markShopInviteAttempted = (userKey: string): void => {
	const current = read();
	if (current) write({ ...current, attemptedFor: userKey });
};
